import { LocalCodingAgentRuntime } from '@this+that/models';

import { DoBoxDelegateComposerTargetKind } from '../../enums';

export interface DoBoxDelegateComposerTarget {
  kind: DoBoxDelegateComposerTargetKind;
  /** LOCAL_CLAUDE only. Unset right after requestDelegate's generic preset —
   *  the composer resolves it to the first selectable paired machine once it
   *  has loaded one (useDoBoxLocalClaudeAvailability). */
  mcpConnectionId?: string;
  deviceName?: string;
  /** AGENT only. */
  agentTemplateId?: string;
  agentName?: string;
}

export const ASSISTANT_COMPOSER_TARGET: DoBoxDelegateComposerTarget = {
  kind: DoBoxDelegateComposerTargetKind.ASSISTANT,
};

export interface DoBoxDelegateComposerEntryState {
  target: DoBoxDelegateComposerTarget;
  /** TECHNICAL_ANALYSIS when true, IMPLEMENT_AND_PR when false — mapped at
   *  submit time (resolveTaskTypeForRuntime also forces this true, disabled,
   *  for the Aider runtime). Meaningless for the ASSISTANT target. */
  planFirst: boolean;
  localRuntime: LocalCodingAgentRuntime;
  localModel: string;
  localAgentProfile: string;
  /** Bumped whenever the composer's text input should pull focus — after
   *  requestDelegate opens the panel, or after a panel-option preset. */
  focusNonce: number;
}

export interface DoBoxDelegateComposerState {
  stateByEntryId: Record<string, DoBoxDelegateComposerEntryState>;
  /** The cloud agent last used or picked on each task. */
  lastAgentByEntryId: Record<string, DoBoxDelegateComposerTarget>;
  /** The cloud agent the owner last used or picked on any task. */
  lastAgent: DoBoxDelegateComposerTarget | null;
  /** Text typed in the card's delegate popover and handed to the detail panel's composer
   *  once, by handOffToPanel; consumeDraft empties it. */
  pendingDraftByEntryId: Record<string, string>;
  /** The entry whose AgentBand should take focus once it renders (after a card delegate). */
  bandFocusEntryId: string | null;
}

export interface DelegatePanelHandOff {
  target: DoBoxDelegateComposerTarget;
  planFirst: boolean;
  draft: string;
}

export type HandlerSet = (
  updater:
    | ((
        state: DoBoxDelegateComposerState,
      ) => Partial<DoBoxDelegateComposerState>)
    | Partial<DoBoxDelegateComposerState>,
) => void;

export interface DoBoxDelegateComposerActions {
  /**
   * Presets the entry's composer to a target (Local Claude by default when
   * none is given) and opens the DoBox detail's hosted panel via
   * useFloatingAssistantStore.requestHostedPanelOpen — the same open path
   * DoBoxCardDelegateButton uses. The caller (the card's own "Delegate"
   * button) still has to open the detail modal itself, exactly like
   * DoBoxCardDelegateButton's onOpen prop does; this action cannot do that
   * part, it only arranges what the panel shows once it's open.
   */
  requestDelegate: (
    entryId: string,
    target?: DoBoxDelegateComposerTarget,
  ) => void;
  /** Presets target + planFirst in one update and bumps focusNonce — used by
   *  requestDelegate and by the panel's deterministic delegate-first option. */
  presetDelegateTarget: (
    entryId: string,
    target: DoBoxDelegateComposerTarget,
    planFirst?: boolean,
  ) => void;
  /** "Open in assistant" from the card popover: presets the composer to the chosen target and
   *  plan choice, stores the typed text for the composer to pick up once, and requests the
   *  hosted panel — the caller still opens the detail modal itself, like requestDelegate. */
  handOffToPanel: (entryId: string, handOff: DelegatePanelHandOff) => void;
  /** Returns the pending draft for the entry and removes it; null when there is none. */
  consumeDraft: (entryId: string) => string | null;
  requestBandFocus: (entryId: string) => void;
  clearBandFocus: (entryId: string) => void;
  setTarget: (entryId: string, target: DoBoxDelegateComposerTarget) => void;
  setPlanFirst: (entryId: string, planFirst: boolean) => void;
  setLocalRuntime: (entryId: string, runtime: LocalCodingAgentRuntime) => void;
  setLocalModel: (entryId: string, model: string) => void;
  setLocalAgentProfile: (entryId: string, profile: string) => void;
  recordAgentTarget: (
    entryId: string,
    target: DoBoxDelegateComposerTarget,
  ) => void;
  requestFocus: (entryId: string) => void;
  /** Reverts the entry to its default (Assistant) state — called after a
   *  successful delegate submit, so the next message defaults back to the
   *  assistant rather than silently re-delegating. */
  clearEntry: (entryId: string) => void;
  reset: () => void;
}

export type DoBoxDelegateComposerStore = DoBoxDelegateComposerState &
  DoBoxDelegateComposerActions;
